import { Badge } from "@choose/ui/badge";
import { Button } from "@choose/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@choose/ui/dropdown-menu";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@choose/ui/hover-card";
import { Separator } from "@choose/ui/separator";
import { Spinner } from "@choose/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@choose/ui/tabs";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CandyOffIcon,
  CircleAlertIcon,
  CopyIcon,
  EllipsisIcon,
  PackageSearchIcon,
  ServerCrashIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  DEFAULT_COLUMN_VISIBILITY as ORDER_DEFAULT_COLUMN_VISIBILITY,
  OrderList,
  TABLE_ACCESSOR_AMOUNT,
  TABLE_ACCESSOR_CREATED_AT,
  TABLE_ACCESSOR_ERROR,
  TABLE_ACCESSOR_ID,
  TABLE_ACCESSOR_IS_RESHIP,
  TABLE_ACCESSOR_RECIPIENT_NAME,
  TABLE_ACCESSOR_SHIPPING_STATUS,
} from "~/components/common/orders/order-list";
import { UserPreview } from "~/components/common/previews/user-preview";
import { Assignee } from "~/components/ui/assignee";
import { Copy } from "~/components/ui/copy";
import type {
  OrderFlowQuery,
  Orders_Search_Input,
} from "~/graphql/graphql.gen";
import {
  Orders_Search_Sort_Field_Input,
  Orders_Search_Sort_Order_Input,
  useInfiniteSearchOrdersQuery,
  useOrderFlowQuery,
} from "~/graphql/graphql.gen";
import { formatDate } from "~/libs/date";
import { toLocale } from "~/libs/locale";
import {
  getGroupActionReasons,
  getGroupActionStatuses,
} from "~/stores/group-actions-store";

export const Route = createFileRoute(
  "/_auth/_app_shell/_main-navigation/group-action/$groupActionId",
)({
  component: RouteComponent,
});

type OrderFlow = NonNullable<NonNullable<OrderFlowQuery>["support_orderFlow"]>;

function RouteComponent() {
  const { i18n, t } = useTranslation();
  const { groupActionId } = Route.useParams();

  const {
    data: orderFlow,
    isLoading,
    error,
  } = useOrderFlowQuery(
    {
      input: {
        orderFlowId: groupActionId,
      },
    },
    {
      select: (data) => data.support_orderFlow,
    },
  );

  if (isLoading) {
    return (
      <>
        <title>{t("app_name")}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full flex-col items-center justify-center">
              <Empty className="min-h-[300px] w-full max-w-md">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Spinner className="size-4" />
                  </EmptyMedia>
                  <EmptyTitle>{t("group_action.loading.title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("group_action.loading.description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );
  }

  if (
    !orderFlow ||
    (error as Error | undefined)?.message === "order_flow_not_found"
  )
    return (
      <>
        <title>{`${t("group_action.not_found.title")} - ${t("app_name")}`}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full justify-center overflow-hidden">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <CandyOffIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t("group_action.not_found.title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("group_action.not_found.description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );

  if (error) {
    return (
      <>
        <title>{t("app_name")}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full flex-col justify-center">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <ServerCrashIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t("group_action.error")}</EmptyTitle>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );
  }

  const groupActionReason = getGroupActionReasons(t).find(
    (reason) => reason.value === orderFlow.flow,
  );
  const groupActionStatus = getGroupActionStatuses(t).find(
    (status) => status.value === orderFlow.status,
  );

  return (
    <>
      <title>{`${groupActionId.substring(11)} - ${t("app_name")}`}</title>

      <div className="flex w-full flex-col md:flex-row">
        <div className="bg-background mb-1 w-full shrink-0 rounded-lg border px-4 py-6 md:mr-1 md:mb-0 md:w-[356px]">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 truncate">
              <span className="truncate">{groupActionId.substring(11)}</span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button variant="ghost" size="sm">
                    <EllipsisIcon />
                  </Button>
                }
              />
              <DropdownMenuContent align="end" className="min-w-3xs">
                <DropdownMenuGroup>
                  <DropdownMenuLabel>
                    {t("group_action.actions.group_action.title")}
                  </DropdownMenuLabel>
                  <Copy
                    content={groupActionId}
                    render={
                      <DropdownMenuItem className="gap-2">
                        <CopyIcon className="text-muted-foreground size-4" />
                        <span>
                          {t(
                            "group_action.actions.group_action.copy_group_action_id",
                          )}
                        </span>
                      </DropdownMenuItem>
                    }
                  />
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="mt-4 text-sm">
            {groupActionReason ? (
              <div className="flex items-center">
                <Copy
                  content={groupActionReason.label}
                  className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                >
                  {t("group_action.reason")}
                </Copy>
                <div className="flex min-h-8 items-center px-3">
                  {groupActionReason.label}
                </div>
              </div>
            ) : null}
            {groupActionStatus ? (
              <div className="flex items-center">
                <Copy
                  content={groupActionStatus.label}
                  className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                >
                  {t("group_action.status")}
                </Copy>
                <div className="flex min-h-8 items-center px-3">
                  <div className="flex items-center gap-2">
                    <groupActionStatus.icon
                      className="size-4"
                      style={{ color: groupActionStatus.color }}
                    />
                    {groupActionStatus.label}
                  </div>
                </div>
              </div>
            ) : null}
            {orderFlow.errors.length > 0 ? (
              <div className="flex items-center">
                <Copy
                  content={orderFlow.errors.length.toString()}
                  className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                >
                  {t("group_action.errors")}
                </Copy>
                <div className="flex min-h-8 items-center px-3">
                  <div className="flex items-center gap-2">
                    <CircleAlertIcon className="size-4 text-red-600" />
                    {orderFlow.errors.length}
                  </div>
                </div>
              </div>
            ) : null}
            <div className="flex items-center">
              <Copy
                content={
                  orderFlow.createdBy.__typename === "ActorBotUser"
                    ? orderFlow.createdBy.botUser.name
                    : orderFlow.createdBy.__typename === "ActorSupplierUser"
                      ? `${orderFlow.createdBy.supplierUser.firstName} ${orderFlow.createdBy.supplierUser.lastName}`
                      : `${orderFlow.createdBy.internalUser.firstName} ${orderFlow.createdBy.internalUser.lastName}`
                }
                className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
              >
                {t("group_action.created_by")}
              </Copy>
              <div className="flex min-h-8 items-center px-3">
                <HoverCard>
                  <HoverCardTrigger
                    render={
                      <div>
                        <Assignee assignee={orderFlow.createdBy} />
                      </div>
                    }
                  />
                  <HoverCardContent>
                    <UserPreview userId={orderFlow.createdBy.id} />
                  </HoverCardContent>
                </HoverCard>
              </div>
            </div>
            <div className="flex items-center">
              <Copy
                content={new Date(orderFlow.createdAt).toUTCString()}
                className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
              >
                {t("group_action.created_at")}
              </Copy>
              <div className="flex min-h-8 items-center px-3">
                {formatDate(
                  new Date(orderFlow.createdAt),
                  toLocale(i18n.language),
                  {
                    dateStyle: "medium",
                    timeStyle: "short",
                  },
                )}
              </div>
            </div>

            <div className="my-2">
              <Separator />
            </div>

            {orderFlow.__typename !==
            "SupportOrderFlowCommunicationAppUsers" ? (
              <div className="flex items-center">
                <Copy
                  content={orderFlow.filters.sale.name}
                  className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                >
                  {t("order.sale")}
                </Copy>
                <div className="flex min-h-8 items-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    nativeButton={false}
                    render={
                      <Link
                        to={`/sale/$saleId`}
                        params={{ saleId: orderFlow.filters.sale.id }}
                      >
                        {orderFlow.filters.sale.name}
                      </Link>
                    }
                  />
                </div>
              </div>
            ) : null}
            {orderFlow.__typename === "SupportOrderFlowProductOutOfStock" ||
            orderFlow.__typename ===
              "SupportOrderFlowProductWrongDescription" ? (
              <div className="flex items-start">
                <Copy
                  content={orderFlow.filters.productReferences.join(", ")}
                  className="text-muted-foreground shrink-0 basis-[92px] truncate pt-1.5 text-left text-sm"
                >
                  {t("group_action.product_references")}
                </Copy>
                <div className="flex min-h-8 flex-wrap items-center gap-1 px-3 py-1.25">
                  {orderFlow.filters.productReferences.map((reference) => (
                    <Badge key={reference} variant="default">
                      {reference}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <div className="bg-background w-full overflow-hidden rounded-lg border">
          {orderFlow.__typename !== "SupportOrderFlowCommunicationAppUsers" ? (
            <Tabs defaultValue="orders" className="flex h-full grow flex-col">
              <TabsList className="mx-7 my-6">
                <TabsTrigger value="orders">
                  {t("group_action.tabs.orders")}
                </TabsTrigger>
              </TabsList>
              <TabsContent
                value="orders"
                className="h-full w-full overflow-auto"
              >
                <OrdersTab
                  orderIds={orderFlow.orderIds}
                  errors={orderFlow.errors}
                />
              </TabsContent>
            </Tabs>
          ) : null}
        </div>
      </div>
    </>
  );
}

function OrdersTab({
  orderIds,
  errors,
}: {
  orderIds: string[];
  errors: OrderFlow["errors"];
}) {
  const { t } = useTranslation();

  const query: Orders_Search_Input = {
    filter: {
      orderIds: orderIds,
    },
    sort: {
      field: Orders_Search_Sort_Field_Input.CreationDate,
      order: Orders_Search_Sort_Order_Input.Descending,
    },
    pageInfo: {
      first: 50,
    },
  };
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteSearchOrdersQuery(
    {
      input: {
        ...query,
      },
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        return lastPage.orders_search.pageInfo.hasNextPage
          ? {
              input: {
                ...query,
                pageInfo: {
                  ...query.pageInfo,
                  after: lastPage.orders_search.pageInfo.endCursor,
                },
              },
            }
          : undefined;
      },
      select: (data) => data.pages.map((page) => page.orders_search),
    },
  );

  const orders =
    data
      ?.flatMap((page) => page.edges)
      .map((edge) => ({
        ...edge.node,
        error:
          errors.find((e) => e.orderId === edge.node.id)?.errorCode ?? null,
      })) ?? [];

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty className="min-h-[300px] w-full max-w-md">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Spinner className="size-4" />
            </EmptyMedia>
            <EmptyTitle>{t("orders.loading")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ServerCrashIcon />
            </EmptyMedia>
            <EmptyTitle>{t("orders.error")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <PackageSearchIcon />
            </EmptyMedia>
            <EmptyTitle>
              {t("group_action.orders.search.empty.title")}
            </EmptyTitle>
            <EmptyDescription>
              {t("group_action.orders.search.empty.description")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <OrderList
      data={orders}
      state={{
        columnOrder: [
          TABLE_ACCESSOR_SHIPPING_STATUS,
          TABLE_ACCESSOR_ID,
          TABLE_ACCESSOR_RECIPIENT_NAME,
          TABLE_ACCESSOR_AMOUNT,
          "alt",
          TABLE_ACCESSOR_ERROR,
          TABLE_ACCESSOR_IS_RESHIP,
          TABLE_ACCESSOR_CREATED_AT,
        ],
        columnPinning: {
          start: ["select"],
          end: [],
        },
        columnVisibility: {
          ...ORDER_DEFAULT_COLUMN_VISIBILITY,
          brandName: false,
          [TABLE_ACCESSOR_ERROR]: true,
        },
      }}
      onFetchMore={() => fetchNextPage()}
      isFetchingMore={isFetchingNextPage}
      hasMore={hasNextPage}
    />
  );
}
